import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  PlayIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { deleteBooklet, getBookletCheck, setBookletStatus } from "../../services/bookletService";
import { getApiErrorCode } from "../../utils/apiError";
import CustomDialog from "../../components/modals/CustomDialog/CustomDialog";
import { Button } from "../../components/forms/Buttons/Button";
import OverflowMenu from "../../components/ui/OverflowMenu";
import { focusRing, Spinner } from "../Exams/TestBuilder/ui";
import { IssueList, uniqueIssues } from "./BookletCheck";
import { bookletErrorMessage, fieldClass, FieldError, footerClass, labelClass, listHref, tapClass, useFocusAfterRender } from "./bts";
import { settleFocus } from "./print/focus";

// The conflicts after which the page reads the booklet again (someone changed it or its status meanwhile).
const STALE = ["booklet_changed", "booklet_active", "exam_archived"];

function DialogError({ children }) {
  if (!children) return null;
  return (
    <div role="alert" className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      {children}
    </div>
  );
}

function ReasonField({ id, value, onChange, error, label }) {
  return (
    <div className="mt-4">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        maxLength={1000}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`${fieldClass} resize-y py-2`}
      />
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
}

/**
 * Activation (a draft, or an archived booklet back to active): reads the check first. Blocking issues stop it;
 * a matrix that is not standard (overridable) can be accepted with a reason. An archived booklet goes back only
 * as it was frozen (changed_since_freeze stops it too). `unsaved`: the cards of the page with unsaved changes the
 * activation would leave out (it freezes the booklet as saved): they stop it until saved or cancelled.
 */
function ActivateDialog({ isOpen, booklet, unsaved = [], onClose, onCheck, onActivated, onStale }) {
  const [t] = useTranslation();
  // Taken when the dialog opens: the booklet's status changes under the closing dialog.
  const [reactivate, setReactivate] = useState(false);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [allow, setAllow] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const focusAfterRender = useFocusAfterRender();

  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    let active = true;
    setReactivate(booklet.status === "archived");
    setCheck(booklet.check || null);
    setAllow(false);
    setReason("");
    setReasonError("");
    setError("");
    setChecking(true);
    getBookletCheck(booklet.uuid, { signal: controller.signal })
      .then((fresh) => {
        if (!active) return;
        setCheck(fresh);
        onCheck(fresh);
      })
      .catch(() => {
        // The check of the page stays; the activation checks again anyway.
      })
      .finally(() => active && setChecking(false));
    return () => {
      active = false;
      controller.abort();
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const issues = check?.issues || [];
  const overridable = issues.filter((i) => i.blocking && i.code === "not_standard" && i.overridable);
  // An archived booklet changed since it was frozen cannot go back as it is: the server refuses it.
  const hard = uniqueIssues(
    issues
      .filter((i) => (i.blocking && !(i.code === "not_standard" && i.overridable)) || (reactivate && i.code === "changed_since_freeze"))
      .map((i) => ({ ...i, blocking: true }))
  );
  const warnings = issues.filter(
    (i) => !i.blocking && !(reactivate && i.code === "changed_since_freeze") && !hard.some((h) => h.code === i.code && h.variant === i.variant)
  );
  const canSubmit = !checking && !hard.length && !unsaved.length && (!overridable.length || allow);

  const submit = async () => {
    if (overridable.length && !reason.trim()) {
      setReasonError(t("bts.status_dialog.reason_required"));
      document.getElementById("bts-activate-reason")?.focus();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const data = await setBookletStatus(booklet.uuid, {
        status: "active",
        revision: booklet.revision,
        ...(overridable.length ? { allow_non_standard: true, reason: reason.trim() } : {}),
      });
      onActivated(data);
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === "booklet_invalid" && err.response?.data?.check) {
        setCheck(err.response.data.check);
        onCheck(err.response.data.check);
        setError(t("bts.status_dialog.invalid"));
        // The issues replace the button: the focus stays in the dialog.
        focusAfterRender("bts-activate-submit", "bts-activate-cancel");
      } else if (STALE.includes(code)) {
        onStale(err);
      } else {
        setError(bookletErrorMessage(t, err, "bts.status_dialog.failed"));
        focusAfterRender("bts-activate-submit", "bts-activate-cancel");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomDialog
      isOpen={isOpen}
      closeModal={() => !saving && onClose()}
      headerText={reactivate ? t("bts.status_dialog.reactivate_title") : t("bts.status_dialog.activate_title")}
      maxWidth="max-w-2xl"
    >
      <div className="text-sm text-slate-600">
        <p className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-sky-900">
          <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {reactivate ? t("bts.status_dialog.reactivate_text") : t("bts.status_dialog.activate_text")}
        </p>
        {unsaved.length > 0 && (
          <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 font-medium text-amber-900">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            {t("bts.status_dialog.unsaved", { cards: unsaved.map((name) => `«${name}»`).join(", ") })}
          </p>
        )}
        <div aria-live="polite">
          {checking && (
            <p className="mt-4 flex items-center gap-2 text-slate-500" role="status">
              <Spinner />
              {t("bts.status_dialog.checking")}
            </p>
          )}
          {!checking && hard.length > 0 && (
            <>
              <p className="mt-4 font-medium text-red-700">{t("bts.status_dialog.cannot", { count: hard.length })}</p>
              <IssueList issues={hard} sections={check?.sections} className="mt-2" />
            </>
          )}
        </div>
        {!checking && !hard.length && overridable.length > 0 && (
          <div className="mt-4">
            <IssueList issues={overridable} sections={check?.sections} />
            <label className="mt-3 flex cursor-pointer select-none items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
              <input
                type="checkbox"
                checked={allow}
                onChange={(e) => setAllow(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
              />
              <span className="font-medium text-slate-700">{t("bts.status_dialog.allow_non_standard")}</span>
            </label>
            {allow && (
              <ReasonField
                id="bts-activate-reason"
                label={t("bts.status_dialog.non_standard_reason")}
                value={reason}
                onChange={(value) => {
                  setReason(value);
                  if (reasonError) setReasonError("");
                }}
                error={reasonError}
              />
            )}
          </div>
        )}
        {!checking && !hard.length && warnings.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 font-medium text-amber-800">{t("bts.status_dialog.warnings", { count: warnings.length })}</p>
            <IssueList issues={warnings} sections={check?.sections} />
          </div>
        )}
        {/* A refused activation lists its issues above: the error only says more without them. */}
        {!hard.length && <DialogError>{error}</DialogError>}
      </div>
      <div className={footerClass}>
        <Button
          id="bts-activate-cancel"
          type="button"
          text={t(hard.length ? "buttons.close" : "buttons.cancel")}
          color="secondary-outline"
          onClick={onClose}
          disabled={saving}
          className={tapClass}
        />
        {!hard.length && (
          <Button
            id="bts-activate-submit"
            type="button"
            color="primary"
            text={reactivate ? t("bts.actions.reactivate") : t("bts.actions.activate")}
            onClick={submit}
            loading={saving}
            disabled={!canSubmit}
            className={tapClass}
          />
        )}
      </div>
    </CustomDialog>
  );
}

// Back to a draft. Once the final PDFs were downloaded, the server asks for a confirmation with a reason
// (400 confirm_required {downloads}): the second step.
function DraftDialog({ isOpen, booklet, onClose, onDone, onStale }) {
  const [t] = useTranslation();
  const [downloads, setDownloads] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const focusAfterRender = useFocusAfterRender();

  useEffect(() => {
    if (!isOpen) return;
    setDownloads(null);
    setReason("");
    setReasonError("");
    setError("");
  }, [isOpen]);

  const submit = async () => {
    const confirming = downloads !== null;
    if (confirming && !reason.trim()) {
      setReasonError(t("bts.status_dialog.reason_required"));
      document.getElementById("bts-draft-reason")?.focus();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const data = await setBookletStatus(booklet.uuid, {
        status: "draft",
        revision: booklet.revision,
        ...(confirming ? { confirm: true, reason: reason.trim() } : {}),
      });
      onDone(data);
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === "confirm_required") {
        setDownloads(err.response?.data?.downloads || 0);
        // The reason field appears: it takes the focus.
        focusAfterRender("bts-draft-reason");
      } else if (STALE.includes(code)) {
        onStale(err);
      } else {
        setError(bookletErrorMessage(t, err, "bts.status_dialog.failed"));
        focusAfterRender("bts-draft-submit");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomDialog isOpen={isOpen} closeModal={() => !saving && onClose()} headerText={t("bts.status_dialog.draft_title")} maxWidth="max-w-xl">
      <div className="text-sm text-slate-600">
        <p>{t("bts.status_dialog.draft_text")}</p>
        {downloads !== null && (
          <div aria-live="polite">
            <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {t("bts.status_dialog.downloaded", { count: downloads })}
            </p>
            <ReasonField
              id="bts-draft-reason"
              label={t("bts.status_dialog.draft_reason")}
              value={reason}
              onChange={(value) => {
                setReason(value);
                if (reasonError) setReasonError("");
              }}
              error={reasonError}
            />
          </div>
        )}
        <DialogError>{error}</DialogError>
      </div>
      <div className={footerClass}>
        <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={onClose} disabled={saving} className={tapClass} />
        <Button
          id="bts-draft-submit"
          type="button"
          color={downloads !== null ? "warning" : "primary"}
          text={downloads !== null ? t("bts.status_dialog.draft_confirm") : t("bts.actions.to_draft")}
          onClick={submit}
          loading={saving}
          className={tapClass}
        />
      </div>
    </CustomDialog>
  );
}

// A plain confirmation: archive, delete.
function ConfirmDialog({ isOpen, id, title, text, confirmText, color = "primary", onClose, onConfirm }) {
  const [t] = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const focusAfterRender = useFocusAfterRender();
  // Deleting leaves the page while the dialog waits for the answer.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (isOpen) setError("");
  }, [isOpen]);

  const confirm = async () => {
    setSaving(true);
    setError("");
    // onConfirm() answers with the error's text, or nothing.
    const message = await onConfirm();
    if (!mounted.current) return;
    setSaving(false);
    if (message) {
      setError(message);
      // The button was disabled while it waited: it takes the focus back.
      focusAfterRender(`${id}-confirm`);
    }
  };

  return (
    <CustomDialog isOpen={isOpen} closeModal={() => !saving && onClose()} headerText={title} maxWidth="max-w-xl">
      <p className="text-sm text-slate-600">{text}</p>
      <DialogError>{error}</DialogError>
      <div className={footerClass}>
        <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={onClose} disabled={saving} className={tapClass} />
        <Button id={`${id}-confirm`} type="button" color={color} text={confirmText} onClick={confirm} loading={saving} className={tapClass} />
      </div>
    </CustomDialog>
  );
}

// The status's step in sight: blue for activating a draft, quiet (icon and text) from md up for the others. Phones:
// the row's first control, framed and as wide as "⋯" leaves it (OverflowMenu's breakpoint).
const STEP_CLASS = {
  primary: `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 max-md:flex-1 md:h-10 ${focusRing}`,
  quiet: `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 max-md:flex-1 md:h-9 md:bg-transparent md:text-slate-600 md:shadow-none md:ring-0 md:hover:bg-slate-100 ${focusRing}`,
};
const MORE_ID = "bts-status-more";

/**
 * The booklet's status actions with their dialogs: a draft → activate (and delete in "⋯"); active → archive (and
 * back to draft in "⋯"); archived → activate again (and back to draft in "⋯"). `onChanged(detail)` takes the
 * server's answer; `onStale(err)` reads the booklet again after a conflict; `onCheck(check)` takes a fresh check;
 * `onReload()` reads the booklet again (a PDF job started elsewhere stops a delete). `unsaved`: the cards with
 * unsaved changes (see ActivateDialog).
 */
export default function BookletStatusActions({ booklet, unsaved = [], onChanged, onStale, onCheck, onReload }) {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [dialog, setDialog] = useState(null);
  const rootRef = useRef(null);
  const stepRef = useRef(null);
  // What opened the dialog ("step" or "more"): the focus goes back there when it closes. The menu's item is gone by
  // then, so "⋯" takes it; settleFocus leaves a focus Headless UI gave back alone.
  const openerRef = useRef("step");
  const open = (name, opener) => {
    openerRef.current = opener;
    setDialog(name);
  };
  const close = () => {
    setDialog(null);
    settleFocus(() => (openerRef.current === "more" ? rootRef.current?.querySelector(`[data-testid="${MORE_ID}"]`) : stepRef.current));
  };
  const stale = (err) => {
    close();
    onStale(err);
    return null;
  };

  const archive = async () => {
    try {
      const data = await setBookletStatus(booklet.uuid, { status: "archived", revision: booklet.revision });
      close();
      onChanged(data);
      toast.success(t("bts.toast.archived"));
      return null;
    } catch (err) {
      if (STALE.includes(getApiErrorCode(err))) return stale(err);
      return bookletErrorMessage(t, err, "bts.status_dialog.failed");
    }
  };

  const remove = async () => {
    try {
      await deleteBooklet(booklet.uuid);
      toast.success(t("bts.toast.deleted", { name: booklet.name }));
      navigate(listHref(location.state), { replace: true });
      return null;
    } catch (err) {
      const code = getApiErrorCode(err);
      if (STALE.includes(code)) return stale(err);
      if (code === "booklet_job_running") {
        // Its PDFs are being made (maybe started elsewhere), or a job is still stopping (cancelled, or the booklet
        // went back to draft): the page reads the booklet to show the job.
        onReload?.();
        return t(err.response?.data?.job?.cancel_requested ? "bts.delete.job_cancelling" : "bts.delete.job_running");
      }
      return bookletErrorMessage(t, err, "bts.delete.failed");
    }
  };

  const { status } = booklet;
  // One step in sight, always the same <button>: a dialog closing after a status change gives the focus back to it,
  // which then offers the new status's step (never Delete). The rarer ones wait in "⋯".
  const step = {
    draft: { key: "activate", look: "primary", Icon: PlayIcon, text: t("bts.actions.activate") },
    active: { key: "archive", look: "quiet", Icon: ArchiveBoxIcon, text: t("bts.actions.archive") },
    archived: { key: "activate", look: "quiet", Icon: PlayIcon, text: t("bts.actions.reactivate") },
  }[status];
  const toDraft = { key: "draft", label: t("bts.actions.to_draft"), Icon: ArrowUturnLeftIcon, onClick: () => open("draft", "more") };
  const more = {
    draft: [{ key: "delete", label: t("bts.actions.delete"), Icon: TrashIcon, danger: true, onClick: () => open("delete", "more") }],
    active: [toDraft],
    archived: [toDraft],
  }[status];

  return (
    <div ref={rootRef} className="flex shrink-0 items-center gap-2 max-md:w-full">
      {step && (
        <button ref={stepRef} type="button" onClick={() => open(step.key, "step")} className={STEP_CLASS[step.look]}>
          <step.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {step.text}
        </button>
      )}
      <OverflowMenu items={more} label={t("bts.actions.more")} testId={MORE_ID} />

      <ActivateDialog
        isOpen={dialog === "activate"}
        booklet={booklet}
        unsaved={unsaved}
        onClose={close}
        onCheck={onCheck}
        onStale={stale}
        onActivated={(data) => {
          close();
          onChanged(data);
          toast.success(t(status === "archived" ? "bts.toast.reactivated" : "bts.toast.activated"));
        }}
      />
      <DraftDialog
        isOpen={dialog === "draft"}
        booklet={booklet}
        onClose={close}
        onStale={stale}
        onDone={(data) => {
          close();
          onChanged(data);
          toast.success(t("bts.toast.to_draft"));
        }}
      />
      <ConfirmDialog
        isOpen={dialog === "archive"}
        id="bts-archive"
        title={t("bts.status_dialog.archive_title")}
        text={t("bts.status_dialog.archive_text")}
        confirmText={t("bts.actions.archive")}
        onClose={close}
        onConfirm={archive}
      />
      <ConfirmDialog
        isOpen={dialog === "delete"}
        id="bts-delete"
        title={t("bts.delete.title")}
        text={t("bts.delete.text", { name: booklet.name })}
        confirmText={t("bts.actions.delete")}
        color="error-solid"
        onClose={close}
        onConfirm={remove}
      />
    </div>
  );
}
